import React, { useEffect, useRef, useState } from "react";
import { Trans } from "react-i18next";
import {
  ArrowUpRight,
  CircleArrowRight,
  CircleCheck,
  Code,
  Lightbulb,
  Phone,
  ShoppingBag,
  UsersRound,
  Zap,
} from "lucide-react";
import { siWebflow, siWhatsapp, siWordpress } from "simple-icons";
import { useTranslation } from "../../hooks/useTranslation";

/**
 * Landing sotto il prompt della pagina pubblica: replica la pagina "LANDING
 * PAGE BotAi - DEF" del Figma del sito (PINGUP - Website 2025), senza navbar
 * (c'è già l'header della pagina), senza FAQ, senza la card "Provalo adesso",
 * senza le due card partner sopra il footer e senza il carosello dei clienti
 * (i loghi erano segnaposto). Tutti i pulsanti di prova riportano su al campo
 * del prompt (onTry): è lì che nasce l'assistente. Fa eccezione «Prova anche
 * tu!» della sezione WhatsApp, che porta a PingUp per il marketing su WhatsApp.
 *
 * Da 1280 px (xl) in su misure, posizioni e ritagli sono quelli del Figma a
 * 1440 px (contenitore da 1200): le illustrazioni sono gli stessi scatti del
 * prodotto usati dal designer (pingup-landing-bot/screenshot-designer, in
 * public/landing), posizionati in assoluto dentro le card che li tagliano.
 * Sotto xl le card si impilano e le immagini scorrono nel flusso.
 */

const FONT = "'Outfit', 'Inter', system-ui, sans-serif";
const VIOLA = "#7C6AE6";
const VERDE = "#00E68B";

const PIANI = [
  { id: "base", risposte: 50, fonti: 5, siti: 1, extra: false, stile: "viola" },
  { id: "lite", risposte: 200, fonti: 50, siti: 1, extra: false, stile: "nero", consigliato: true },
  { id: "plus", risposte: 1000, fonti: 100, siti: 2, extra: false, stile: "bianco" },
  { id: "business", risposte: 5000, fonti: 500, siti: 10, extra: true, stile: "bianco" },
];


const USI = [
  { Icona: Phone, scuro: false },
  { Icona: UsersRound, scuro: false },
  { Icona: Lightbulb, scuro: false },
  { Icona: ShoppingBag, scuro: true },
];

const Contenitore = ({ className = "", children }) => (
  <div className={`mx-auto w-full max-w-[1200px] px-4 md:px-6 xl:px-0 ${className}`}>
    {children}
  </div>
);

const Marchio = ({ icona, className = "", colore }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path d={icona.path} fill={colore || "currentColor"} />
  </svg>
);

const Immagine = ({ src, alt = "", className = "" }) => (
  <img
    src={src}
    alt={alt}
    loading="lazy"
    decoding="async"
    className={`max-w-none select-none ${className}`}
    draggable={false}
  />
);

// La larghezza del sottotitolo decide dove va a capo, come nel Figma.
const TitoloSezione = ({ titolo, sottotitolo, larghezza = "max-w-[600px]" }) => (
  <div className="mx-auto max-w-[800px] text-center">
    <h2 className="text-[32px] font-medium leading-[1.2] tracking-[-0.01em] md:text-[48px]">
      {titolo}
    </h2>
    {sottotitolo && (
      <p className={`mx-auto mt-[14px] text-[16px] leading-7 text-[#7A7A7A] md:text-[18px] ${larghezza}`}>
        {sottotitolo}
      </p>
    )}
  </div>
);

// Titolo a sinistra e testo a destra, come in "Tutto quello che ti serve" e
// "Un assistente AI per ogni esigenza".
const TitoloAffiancato = ({ titolo, testo, larghezzaTesto = "" }) => (
  <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
    <h2 className="max-w-[640px] text-[32px] font-medium leading-[1.25] tracking-[-0.01em] md:text-[48px]">
      {titolo}
    </h2>
    <p className="max-w-[432px] text-[16px] leading-7 text-[#7A7A7A] md:w-[432px] md:shrink-0 md:text-[18px]">
      <span className={`block ${larghezzaTesto}`}>{testo}</span>
    </p>
  </div>
);

const BottoneProva = ({ onClick, children, className = "", misura = "h-11 text-[15px]" }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 font-medium text-white transition-colors hover:brightness-95 ${misura} ${className}`}
    style={{ backgroundColor: VIOLA }}
  >
    {children}
    <Zap className="h-4 w-4" />
  </button>
);

const BottoneChiaro = ({ onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex h-11 items-center justify-center rounded-lg border border-[#EFEFEF] bg-[#F7F7F7] px-4 text-[15px] font-medium text-[#121316] transition-colors hover:bg-[#EFEFEF]"
  >
    {children}
  </button>
);

const Card = ({ className = "", children }) => (
  <div className={`relative overflow-hidden rounded-[20px] border border-[#F0F0F0] bg-[#F7F7F7] ${className}`}>
    {children}
  </div>
);

// Ingresso morbido allo scorrimento: il blocco parte poco più in basso e
// trasparente e sale al suo posto quando entra nello schermo, una volta sola.
// `ritardo` (ms) scala le card di una stessa riga. Senza IntersectionObserver
// e con «riduci movimento» nel sistema si vede tutto subito (vedi lo stile
// .pu-rivela in LandingSections).
const Rivela = ({ className = "", ritardo = 0, style, children, ...resto }) => {
  const ref = useRef(null);
  const [visibile, setVisibile] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setVisibile(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([voce]) => {
        if (voce.isIntersecting) {
          setVisibile(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -6% 0px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`pu-rivela ${visibile ? "pu-rivela-visibile" : ""} ${className}`}
      style={{ ...(ritardo ? { transitionDelay: `${ritardo}ms` } : {}), ...style }}
      {...resto}
    >
      {children}
    </div>
  );
};

// Il video dell'onboarding (lo stesso fatto in Remotion per la presentazione)
// parte solo quando il riquadro è in vista e si ferma quando esce: niente
// download a pagina appena aperta, dove si vede solo il prompt.
const VideoOnboarding = ({ lingua, etichetta }) => {
  const videoRef = useRef(null);
  const sorgente = lingua?.startsWith("it") ? "it" : "en";

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches) {
      return undefined;
    }
    // Browser senza IntersectionObserver: il video parte e basta.
    if (typeof IntersectionObserver === "undefined") {
      video.play?.()?.catch?.(() => {});
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) video.play().catch(() => {});
        else video.pause();
      },
      { threshold: 0.25 }
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, [sorgente]);

  return (
    <div
      className="relative aspect-video overflow-hidden rounded-[20px] md:rounded-[24px] xl:aspect-auto xl:h-[660px]"
      style={{
        backgroundImage:
          "linear-gradient(146deg, #F0F4FF 0%, #F0FFF4 50%, #F8F0FF 100%)",
      }}
    >
      <video
        key={sorgente}
        ref={videoRef}
        className="h-full w-full object-cover"
        src={`/landing/video-${sorgente}.mp4`}
        poster={`/landing/video-${sorgente}.jpg`}
        muted
        loop
        playsInline
        preload="none"
        aria-label={etichetta}
      />
    </div>
  );
};

const LandingSections = ({ onTry }) => {
  const { t, currentLanguage } = useTranslation();

  const vaiA = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  const plurale = (chiave, n) => t(chiave, { count: n, n });
  const lista = (chiave) => {
    const valore = t(chiave, { returnObjects: true });
    return Array.isArray(valore) ? valore : [];
  };

  const grassetto = { b: <strong className="font-medium text-[#4A4A4A]" /> };

  return (
    <div className="overflow-x-hidden bg-white text-[#121316]" style={{ fontFamily: FONT }}>
      <style>{`
        .pu-rivela { opacity: 0; transform: translateY(22px); transition: opacity 0.8s cubic-bezier(0.22, 1, 0.36, 1), transform 0.8s cubic-bezier(0.22, 1, 0.36, 1); }
        .pu-rivela-visibile { opacity: 1; transform: none; }
        @media (prefers-reduced-motion: reduce) {
          .pu-rivela { opacity: 1; transform: none; transition: none; }
        }
      `}</style>

      {/* Hero: titolo, testo e pulsanti, poi il video in loop */}
      <section className="pt-20 md:pt-28">
        <Contenitore className="grid gap-8 md:grid-cols-[1fr_minmax(0,500px)] md:gap-10">
          <Rivela>
            <h2 className="text-[38px] font-medium leading-[1.2] tracking-[-0.01em] md:text-[56px]">
              {t("landing.hero.titleBefore")}{" "}
              <span className="rounded-lg bg-[#C4B0FC] px-2.5 [box-decoration-break:clone] md:px-3">
                {t("landing.hero.titleHighlight")}
              </span>
              <br className="hidden md:block" /> {t("landing.hero.titleLine2")}
              <br className="hidden md:block" /> {t("landing.hero.titleLine3")}
            </h2>
          </Rivela>
          <Rivela ritardo={120}>
            <p className="text-[16px] leading-[27px] text-[#7A7A7A] md:text-[18px]">
              <Trans i18nKey="landing.hero.text" components={grassetto} />
            </p>
            <p className="mt-3 text-[12px] font-semibold uppercase leading-[1.8] tracking-[0.04em] md:text-[13px]" style={{ color: VIOLA }}>
              {lista("landing.hero.features").join("  ·  ")}
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <BottoneProva onClick={onTry}>{t("landing.hero.cta")}</BottoneProva>
              <BottoneChiaro onClick={() => vaiA("come-funziona")}>
                {t("landing.hero.how")}
              </BottoneChiaro>
            </div>
          </Rivela>
        </Contenitore>
        <Contenitore className="mt-12 xl:mt-[60px]">
          <Rivela>
            <VideoOnboarding lingua={currentLanguage} etichetta={t("landing.hero.videoLabel")} />
          </Rivela>
        </Contenitore>
      </section>

      {/* Più di una semplice chat sul sito */}
      <section className="pt-24 xl:pt-[85px]">
        <Contenitore>
          <Rivela>
            <TitoloSezione
              titolo={t("landing.more.title")}
              sottotitolo={t("landing.more.subtitle")}
            />
          </Rivela>
          <div className="mt-14 grid gap-12 md:grid-cols-3 md:gap-8 xl:grid-cols-[390px_392px_1fr] xl:gap-0 xl:pl-[60px]">
            {lista("landing.more.items").map((voce, i) => {
              const colori = [
                ["#E6DFFF", "#C4B5FA"],
                ["#E6DFFF", VIOLA],
                ["#C9F6E2", "#14DB8C"],
              ][i];
              return (
                <Rivela key={voce.label} ritardo={i * 110} className="max-w-[300px]">
                  <div className="flex items-center gap-4">
                    <span className="text-[52px] font-medium leading-none tracking-[-0.02em]">
                      <span style={{ color: colori[0] }}>0</span>
                      <span style={{ color: colori[1] }}>{i + 1}</span>
                    </span>
                    <span className="text-[18px] font-semibold uppercase tracking-[0.06em]">
                      · {voce.label}
                    </span>
                  </div>
                  <h3 className="mt-[22px] text-[18px] font-medium leading-6">{voce.title}</h3>
                  <p className="mt-2 text-[16px] leading-7 text-[#7A7A7A]">{voce.text}</p>
                </Rivela>
              );
            })}
          </div>
        </Contenitore>
      </section>

      {/* Attivalo sul tuo sito in pochi passaggi */}
      <section id="come-funziona" className="scroll-mt-6 pt-24 xl:pt-[158px]">
        <Contenitore>
          <Rivela>
            <TitoloSezione
              titolo={t("landing.steps.title")}
              sottotitolo={t("landing.steps.subtitle")}
              larghezza="max-w-[680px]"
            />
          </Rivela>
          <div className="mx-auto mt-12 grid max-w-[560px] gap-6 xl:mt-[62px] xl:max-w-none xl:grid-cols-3">
            {lista("landing.steps.items").map((passo, i) => (
              <Rivela key={passo.title} ritardo={i * 110}>
              <Card className="flex h-full flex-col xl:h-[698px]">
                <div className="px-6 pt-8 xl:px-10 xl:pt-[42px]">
                  <span className="inline-block h-7 rounded-md bg-[#E8E2FF] px-2.5 text-[12px] font-semibold uppercase leading-7 tracking-[0.04em] text-[#5B47D6]">
                    {t("landing.steps.label", { n: i + 1 })}
                  </span>
                  <h3 className="mt-[23px] text-[26px] font-medium leading-[34px] xl:text-[30px] xl:leading-[38px]">
                    {passo.title}
                  </h3>
                  <p className="mt-[9px] text-[16px] leading-7 text-[#7A7A7A]">{passo.text}</p>
                </div>
                {/* Illustrazione ancorata al fondo della card, che la taglia */}
                <div className="relative mt-8 h-[300px] xl:mt-auto xl:h-[340px]">
                  {i === 0 && (
                    <Immagine src="/landing/fonti.webp" alt={t("landing.alt.sources")} className="absolute bottom-[-126px] left-[4px] w-[430px]" />
                  )}
                  {i === 1 && (
                    <Immagine src="/landing/stili-widget.webp" alt={t("landing.alt.styles")} className="absolute bottom-[-30px] left-[10px] w-[414px]" />
                  )}
                  {i === 2 && (
                    <>
                      <Immagine src="/landing/conversazioni.webp" alt={t("landing.alt.conversations")} className="absolute bottom-[-113px] left-[31px] w-[383px]" />
                      <img src="/landing/cursore.svg" alt="" className="absolute bottom-[150px] left-[364px] h-6 w-6" draggable={false} />
                    </>
                  )}
                </div>
              </Card>
              </Rivela>
            ))}
          </div>
          <Rivela className="mt-12 flex flex-wrap justify-center gap-3 xl:mt-[60px]">
            <BottoneProva onClick={onTry}>{t("landing.steps.cta")}</BottoneProva>
            <BottoneChiaro onClick={() => vaiA("domanda-risposta")}>
              {t("landing.hero.how")}
            </BottoneChiaro>
          </Rivela>
        </Contenitore>
      </section>

      {/* Domanda, risposta, azione! */}
      <section id="domanda-risposta" className="scroll-mt-6 pt-24 xl:pt-[165px]">
        <Contenitore>
          <Rivela>
            <TitoloSezione
              titolo={t("landing.actions.title")}
              sottotitolo={t("landing.actions.subtitle")}
            />
          </Rivela>
          <div className="mt-12 space-y-6 xl:mt-[46px]">
            <Rivela>
            <Card className="px-6 pt-8 xl:flex xl:h-[500px] xl:items-center xl:p-0">
              <div className="xl:ml-20">
                <h3 className="max-w-[450px] text-[28px] font-medium leading-[36px] xl:text-[36px] xl:leading-[44px]">
                  {t("landing.actions.answers.title")}
                </h3>
                <p className="mt-6 max-w-[400px] text-[16px] leading-7 text-[#7A7A7A]">
                  <Trans i18nKey="landing.actions.answers.text1" components={grassetto} />
                </p>
                <p className="mt-4 max-w-[400px] text-[16px] leading-7 text-[#7A7A7A]">
                  {t("landing.actions.answers.text2")}
                </p>
              </div>
              <div className="relative mt-6 h-[320px] xl:absolute xl:inset-0 xl:mt-0 xl:h-auto">
                <Immagine src="/landing/risposta-fonti.webp" alt={t("landing.alt.answer")} className="absolute -left-2 top-0 w-[480px] xl:left-[549px] xl:top-[24px] xl:w-[667px]" />
              </div>
            </Card>
            </Rivela>

            <Rivela>
            <Card className="px-6 pt-8 xl:flex xl:h-[520px] xl:items-center xl:p-0">
              <div className="max-w-[400px] xl:ml-[720px]">
                <h3 className="text-[28px] font-medium leading-[36px] xl:text-[36px] xl:leading-[44px]">
                  {t("landing.actions.guide.title")}
                </h3>
                <ul className="mt-6 text-[16px] leading-[27px] text-[#7A7A7A] xl:text-[18px]">
                  {lista("landing.actions.guide.items").map((voce) => (
                    <li key={voce} className="flex items-center gap-3">
                      <span className="h-1 w-1 shrink-0 rounded-full bg-[#7A7A7A]" />
                      {voce}
                    </li>
                  ))}
                </ul>
                <p className="mt-6 text-[16px] leading-7 text-[#7A7A7A]">
                  {t("landing.actions.guide.note")}
                </p>
              </div>
              <div className="relative mt-6 h-[300px] xl:absolute xl:inset-0 xl:mt-0 xl:h-auto">
                <Immagine src="/landing/modulo-whatsapp.webp" alt={t("landing.alt.contacts")} className="absolute left-1/2 top-0 w-[380px] -translate-x-1/2 xl:left-[43px] xl:top-[20px] xl:w-[580px] xl:translate-x-0" />
              </div>
            </Card>
            </Rivela>

            <Rivela>
            <Card className="px-6 pt-8 xl:flex xl:h-[562px] xl:items-center xl:p-0">
              <div className="xl:ml-20">
                <h3 className="max-w-[450px] text-[28px] font-medium leading-[36px] xl:text-[36px] xl:leading-[44px]">
                  {t("landing.actions.insights.title")}
                </h3>
                <p className="mt-6 max-w-[400px] text-[16px] leading-7 text-[#7A7A7A]">
                  {t("landing.actions.insights.text1")}
                </p>
                <p className="mt-4 max-w-[400px] text-[16px] leading-7 text-[#4A4A4A]">
                  {t("landing.actions.insights.text2")}
                </p>
              </div>
              <div className="relative mt-6 h-[300px] xl:absolute xl:inset-0 xl:mt-0 xl:h-auto">
                <Immagine src="/landing/analisi.webp" alt={t("landing.alt.insights")} className="absolute left-0 top-0 w-[400px] xl:left-[612px] xl:top-[20px] xl:w-[658px]" />
              </div>
            </Card>
            </Rivela>

            {/* La card verde di WhatsApp */}
            <Rivela
              className="relative overflow-hidden rounded-[20px] px-6 pt-10 xl:h-[500px] xl:p-0"
              style={{
                backgroundImage:
                  "linear-gradient(90deg, #1F294B 0%, #1B2D48 22%, #0F3438 48%, #103826 75%, #10381D 100%)",
              }}
            >
              <div className="xl:ml-[640px] xl:pt-[106px]">
                <span className="inline-flex h-[31px] items-center rounded-md px-3 text-[13px] font-semibold uppercase tracking-[0.06em] text-[#0B2A1A] xl:text-[15px]" style={{ backgroundColor: VERDE }}>
                  {t("landing.whatsapp.badge")}
                </span>
                <h3 className="mt-[23px] max-w-[440px] text-[28px] font-medium leading-[36px] text-white xl:text-[36px] xl:leading-[44px]">
                  {t("landing.whatsapp.titleBefore")}{" "}
                  <span className="inline-flex items-center gap-2 whitespace-nowrap align-middle text-[#25D366]">
                    <Marchio icona={siWhatsapp} className="h-[1.2em] w-[1.2em]" />
                    {t("landing.whatsapp.brand")}
                  </span>
                </h3>
                <p className="mt-5 max-w-[460px] text-[16px] leading-7 text-white/85">
                  {t("landing.whatsapp.text")}
                </p>
                {/* Unico pulsante che non torna al prompt: porta a PingUp
                    per il marketing su WhatsApp. */}
                <a
                  href="https://web.pingup.ai/marketing"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-[22px] inline-flex items-center gap-2 text-[16px] font-medium underline underline-offset-4"
                  style={{ color: VERDE }}
                >
                  {t("landing.whatsapp.cta")}
                  <ArrowUpRight className="h-5 w-5" />
                </a>
              </div>
              {/* Telefono con prenotazione, broadcast e promemoria: un'unica
                  immagine del designer (02/11), al posto del telefono ricostruito. */}
              {/* Sotto xl l'immagine sta nel flusso, centrata: così la card finisce
                  esattamente dove finisce il telefono (che nell'immagine è tagliato
                  dritto) e non resta nessuna striscia sotto. Su xl è assoluta, con il
                  fondo a 500px come la card. */}
              <div className="mt-10 flex justify-center xl:absolute xl:left-[60px] xl:top-[26px] xl:mt-0 xl:block">
                <Immagine src="/landing/whatsapp-prenotazioni.webp" alt={t("landing.alt.whatsapp")} className="block w-[340px] shrink-0 xl:w-[480px]" />
              </div>
            </Rivela>
          </div>
        </Contenitore>
      </section>

      {/* Tutto quello che ti serve per gestire il tuo assistente AI */}
      <section className="pt-24 xl:pt-[162px]">
        <Contenitore>
          <Rivela>
            <TitoloAffiancato
              titolo={t("landing.tools.title")}
              testo={t("landing.tools.subtitle")}
            />
          </Rivela>
          <div className="mt-12 grid gap-6 xl:grid-cols-12">
            <Rivela className="xl:col-span-7">
            <Card className="h-full xl:h-[348px]">
              <div className="relative z-[1] px-6 pt-8 xl:w-[340px] xl:pl-[60px] xl:pr-0 xl:pt-[58px]">
                <h3 className="text-[26px] font-medium leading-[34px] xl:text-[30px] xl:leading-9">
                  {t("landing.tools.knowledge.title")}
                </h3>
                <p className="mt-[14px] text-[16px] leading-7 text-[#7A7A7A]">
                  {t("landing.tools.knowledge.text1")}
                </p>
                <p className="mt-3 text-[16px] leading-7 text-[#4A4A4A] xl:whitespace-pre-line">
                  {t("landing.tools.knowledge.text2")}
                </p>
              </div>
              <div className="relative mt-6 h-[260px] xl:absolute xl:inset-0 xl:mt-0 xl:h-auto">
                <Immagine src="/landing/fonti.webp" alt={t("landing.alt.sources")} className="absolute left-1/2 top-0 w-[320px] -translate-x-1/2 xl:left-[361px] xl:top-[21px] xl:translate-x-0" />
              </div>
            </Card>
            </Rivela>

            <Rivela className="xl:col-span-5" ritardo={100}>
            <Card className="h-full px-6 py-10 text-center xl:h-[348px] xl:px-10 xl:py-0 xl:pt-10">
              <h3 className="text-[26px] font-medium leading-[34px] xl:text-[30px] xl:leading-9">
                {t("landing.tools.connect.title")}
              </h3>
              <p className="mx-auto mt-[14px] max-w-[380px] text-[16px] leading-7 text-[#7A7A7A]">
                {t("landing.tools.connect.text")}
              </p>
              <div className="mx-auto mt-8 flex max-w-[400px] flex-wrap justify-center gap-x-4 gap-y-[14px] xl:mt-[52px]">
                {[
                  { icona: <Marchio icona={siWordpress} className="h-5 w-5" colore="#21759B" />, nome: "WordPress", tipo: t("landing.tools.connect.plugin") },
                  { icona: <Marchio icona={siWebflow} className="h-5 w-5" colore="#146EF5" />, nome: "Webflow", tipo: t("landing.tools.connect.code") },
                  { icona: <Code className="h-5 w-5" />, nome: t("landing.tools.connect.anySite"), tipo: t("landing.tools.connect.code") },
                ].map((p) => (
                  <span key={p.nome} className="inline-flex h-11 items-center gap-2 rounded-full bg-white px-4 text-[15px] font-medium shadow-[0_2px_10px_rgba(0,0,0,0.06)]">
                    {p.icona}
                    {p.nome}
                    <span className="rounded bg-[#F3F3F3] px-1.5 py-0.5 text-[10px] font-normal text-[#7A7A7A]">
                      {p.tipo}
                    </span>
                  </span>
                ))}
              </div>
            </Card>
            </Rivela>

            <Rivela className="xl:col-span-6">
            <Card className="h-full xl:h-[458px]">
              <div className="relative h-[230px] xl:absolute xl:inset-x-0 xl:top-0 xl:h-[260px]">
                <Immagine src="/landing/panoramica.webp" alt={t("landing.alt.overview")} className="absolute left-1/2 top-[-18px] w-[360px] -translate-x-1/2 xl:top-[-23px] xl:w-[463px]" />
              </div>
              <div className="relative px-6 pb-10 text-center xl:px-[60px] xl:pb-0 xl:pt-[276px]">
                <h3 className="text-[26px] font-medium leading-[34px] xl:text-[30px] xl:leading-9">
                  {t("landing.tools.overview.title")}
                </h3>
                <p className="mx-auto mt-3 max-w-[420px] text-[16px] leading-7 text-[#7A7A7A]">
                  {t("landing.tools.overview.text")}
                </p>
                <p className="mt-3 text-[16px] font-medium leading-7 text-[#4A4A4A]">
                  {t("landing.tools.overview.highlight")}
                </p>
              </div>
            </Card>
            </Rivela>

            <Rivela className="xl:col-span-6" ritardo={100}>
            <Card className="h-full xl:h-[458px]">
              <div className="px-6 pt-10 text-center xl:px-[54px] xl:pt-[42px]">
                <h3 className="text-[26px] font-medium leading-[34px] xl:text-[30px] xl:leading-9">
                  {t("landing.tools.customize.title")}
                </h3>
                <p className="mx-auto mt-3 max-w-[480px] text-[16px] leading-7 text-[#7A7A7A]">
                  {t("landing.tools.customize.text")}
                </p>
              </div>
              <div className="relative mt-6 h-[300px] xl:absolute xl:inset-0 xl:mt-0 xl:h-auto">
                <Immagine src="/landing/widget-o-barra.webp" alt={t("landing.alt.widgetOrBar")} className="absolute left-1/2 top-0 w-[560px] -translate-x-1/2 xl:left-[2px] xl:top-[167px] xl:w-[579px] xl:translate-x-0" />
              </div>
            </Card>
            </Rivela>
          </div>
        </Contenitore>
      </section>

      {/* E-commerce */}
      <section className="pt-24 xl:pt-[162px]">
        <Contenitore>
          <Rivela
            className="relative overflow-hidden rounded-[20px] px-6 pt-10 xl:h-[500px] xl:p-0"
            style={{
              backgroundImage:
                "linear-gradient(100deg, #10132B 0%, #131A33 55%, #10302A 100%)",
            }}
          >
            <div className="max-w-[470px] xl:ml-[60px] xl:pt-[70px]">
              <span className="inline-flex h-7 items-center rounded-md px-2.5 text-[12px] font-semibold uppercase tracking-[0.04em] text-[#0B2A1A]" style={{ backgroundColor: VERDE }}>
                {t("landing.ecommerce.badge")}
              </span>
              <h3 className="mt-[23px] text-[28px] font-medium leading-[36px] text-white xl:text-[36px] xl:leading-[46px]">
                {t("landing.ecommerce.title")}
              </h3>
              <p className="mt-[19px] text-[16px] leading-7 text-white/75">
                <Trans i18nKey="landing.ecommerce.text1" components={{ b: <strong className="font-medium text-white" /> }} />
              </p>
              <p className="mt-1 text-[16px] leading-7 text-white/75">
                {t("landing.ecommerce.text2")}
              </p>
              <div className="mt-10 flex flex-wrap gap-3">
                <BottoneProva onClick={onTry}>{t("landing.ecommerce.cta")}</BottoneProva>
                <a
                  href="https://pingup.ai/contact-us"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-11 items-center rounded-lg border border-white/70 px-4 text-[15px] font-medium text-white transition-colors hover:bg-white/10"
                >
                  {t("landing.ecommerce.support")}
                </a>
              </div>
            </div>
            {/* Dalla scena si tiene sito, chat e fonti: la card del contatto
                a destra resta fuori, come nel disegno */}
            {/* Sotto xl il box annulla il padding destro della card (-mr-6): il taglio
                dell'immagine coincide col bordo della card, senza striscia a destra.
                Su xl largo 596 da 604 = 1200, cioè fino al bordo. */}
            <div className="relative -mr-6 mt-8 h-[200px] overflow-hidden sm:h-[300px] xl:absolute xl:left-[604px] xl:top-[53px] xl:mr-0 xl:mt-0 xl:h-[447px] xl:w-[596px]">
              <Immagine src="/landing/ecommerce.webp" alt={t("landing.alt.ecommerce")} className="w-[133%] xl:w-[793px]" />
            </div>
          </Rivela>
        </Contenitore>
      </section>

      {/* Un assistente AI per ogni esigenza del tuo business */}
      <section className="pt-24 xl:pt-[162px]">
        <Contenitore>
          <Rivela>
            <TitoloAffiancato
              titolo={t("landing.useCases.title")}
              testo={t("landing.useCases.subtitle")}
              larghezzaTesto="md:max-w-[390px]"
            />
          </Rivela>
          <div className="mt-12 grid gap-x-6 gap-y-6 md:grid-cols-2 xl:gap-y-7">
            {lista("landing.useCases.items").map((uso, i) => {
              const { Icona, scuro } = USI[i] || USI[0];
              return (
                <Rivela key={uso.title} ritardo={(i % 2) * 100}>
                <Card className="h-full px-6 py-8 xl:h-[260px] xl:px-10 xl:py-0 xl:pt-[46px]">
                  <div className="flex items-center justify-between gap-4">
                    <span
                      className={`rounded-md px-2 py-1 text-[12px] font-medium ${
                        scuro ? "bg-[#121316] text-white" : "bg-[#E8E2FF] text-[#3F2F9C]"
                      }`}
                    >
                      {uso.tag}
                    </span>
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white">
                      <Icona className="h-[18px] w-[18px]" />
                    </span>
                  </div>
                  <h3 className="mt-[17px] text-[22px] font-medium leading-[30px] xl:text-[24px]">{uso.title}</h3>
                  <p className="mt-[9px] max-w-[512px] text-[14px] leading-5 text-[#7A7A7A]">
                    {uso.text}
                  </p>
                  <button
                    type="button"
                    onClick={onTry}
                    className="mt-6 inline-flex items-center gap-2 text-[14px] font-medium hover:underline"
                  >
                    {t("landing.useCases.more")}
                    <CircleArrowRight className="h-4 w-4" />
                  </button>
                </Card>
                </Rivela>
              );
            })}
          </div>
        </Contenitore>
      </section>

      {/* Scegli il piano più adatto a te */}
      <section className="pt-24 xl:pt-[162px]">
        <Contenitore>
          <Rivela className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
            <h2 className="text-[40px] font-medium leading-[1.15] tracking-[-0.01em] md:text-[60px] md:leading-[68px]">
              {t("landing.pricing.titleBefore")}
              <br />
              {t("landing.pricing.titleMiddle")}{" "}
              <span className="rounded-lg bg-[#C4B0FC] px-2.5 md:px-3">
                {t("landing.pricing.titleHighlight")}
              </span>
            </h2>
            <div className="text-[16px] leading-7 text-[#7A7A7A] md:w-[432px] md:shrink-0">
              {lista("landing.pricing.note").map((riga) => (
                <p key={riga}>{riga}</p>
              ))}
            </div>
          </Rivela>

          <div className="mt-12 grid items-start gap-6 sm:grid-cols-2 xl:mt-[142px] xl:grid-cols-4">
            {PIANI.map((piano, i) => {
              const voci = [
                { ok: true, testo: plurale("landing.pricing.answers", piano.risposte) },
                { ok: true, testo: plurale("landing.pricing.sources", piano.fonti) },
                { ok: true, testo: plurale("landing.pricing.sites", piano.siti) },
                { ok: piano.extra, testo: t("landing.pricing.whatsapp") },
                { ok: piano.extra, testo: t("landing.pricing.productCard") },
                { ok: piano.extra, testo: t("landing.pricing.noLogo") },
              ];
              return (
                <Rivela
                  key={piano.id}
                  ritardo={i * 90}
                  className={`relative rounded-[20px] px-8 pb-10 pt-[42px] xl:px-[38px] ${
                    piano.consigliato
                      ? "border-[1.5px] border-[#121316] bg-white xl:-mt-[60px]"
                      : "border border-[#F0F0F0] bg-[#F7F7F7]"
                  }`}
                >
                  {piano.consigliato && (
                    <span className="absolute -top-3 right-4 rounded-md bg-[#121316] px-2.5 py-1 text-[12px] font-medium text-white">
                      {t("landing.pricing.recommended")}
                    </span>
                  )}
                  <h3 className="text-[24px] font-medium leading-6">
                    {t(`landing.pricing.plans.${piano.id}.name`)}
                  </h3>
                  <p className="mt-[10px] min-h-[56px] text-[16px] leading-7 text-[#7A7A7A] xl:whitespace-pre-line">
                    {t(`landing.pricing.plans.${piano.id}.desc`)}
                  </p>
                  <p className="mt-[30px] text-[48px] font-medium leading-[48px]">
                    {t(`landing.pricing.plans.${piano.id}.price`)}
                    {piano.id !== "base" && (
                      <span className="ml-1.5 text-[13px] font-normal leading-none text-[#4A4A4A]">
                        {t("landing.pricing.perYear")}
                      </span>
                    )}
                  </p>
                  <p className="mt-[5px] min-h-[52px] text-[18px] leading-[26px] text-[#7A7A7A] xl:whitespace-pre-line">
                    {t(`landing.pricing.plans.${piano.id}.sub`)}
                  </p>
                  <div className="mt-[19px] border-t border-[#E9E9E9] pt-6">
                    <p className="text-[17px] font-medium leading-6">{t("landing.pricing.includes")}</p>
                    <ul className="mt-2">
                      {voci.map((voce) => (
                        <li
                          key={voce.testo}
                          className={`flex items-center gap-2 text-[15px] leading-8 xl:text-[16px] ${voce.ok ? "text-[#4A4A4A]" : "text-[#A8A8A8]"}`}
                        >
                          {voce.ok ? (
                            <CircleCheck className="h-3.5 w-3.5 shrink-0 text-[#19C37D]" />
                          ) : (
                            <span className="w-3.5 shrink-0 text-center">—</span>
                          )}
                          {voce.testo}
                        </li>
                      ))}
                    </ul>
                  </div>
                  {piano.stile === "viola" ? (
                    <BottoneProva onClick={onTry} misura="h-10 text-[14px]" className="mt-[26px] w-full">
                      {t(`landing.pricing.plans.${piano.id}.cta`)}
                    </BottoneProva>
                  ) : (
                    <button
                      type="button"
                      onClick={onTry}
                      className={`mt-[26px] h-10 w-full rounded-lg text-[14px] font-medium transition-colors ${
                        piano.stile === "nero"
                          ? "bg-[#121316] text-white hover:bg-black"
                          : "border border-[#EDEDED] bg-white text-[#121316] hover:bg-[#F3F3F3]"
                      }`}
                    >
                      {t(`landing.pricing.plans.${piano.id}.cta`)}
                    </button>
                  )}
                </Rivela>
              );
            })}
          </div>

          <Rivela
            className="mt-6 flex flex-col gap-5 rounded-[20px] px-6 py-7 md:flex-row md:items-center md:justify-between xl:mt-[42px] xl:h-[138px] xl:px-[60px] xl:py-0"
            style={{
              backgroundImage:
                "linear-gradient(90deg, #17191D 0%, #2B3136 45%, #6D72C9 100%)",
            }}
          >
            <div>
              <p className="text-[22px] font-medium leading-6 text-white md:text-[24px]">
                {t("landing.pricing.custom.titleBefore")}{" "}
                <span className="text-[#B9A8FF]">{t("landing.pricing.custom.titleHighlight")}</span>
              </p>
              <p className="mt-5 max-w-[560px] text-[16px] leading-6 text-white/70 md:whitespace-pre-line">
                {t("landing.pricing.custom.text")}
              </p>
            </div>
            <a
              href="https://pingup.ai/contact-us"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-11 w-fit shrink-0 items-center rounded-lg bg-[#C9B8FF] px-5 text-[14px] font-medium text-[#121316] transition-colors hover:bg-[#BBA6FF]"
            >
              {t("landing.pricing.custom.cta")}
            </a>
          </Rivela>
        </Contenitore>
      </section>

      {/* Footer (senza le card partner) */}
      <footer className="pt-24 xl:pt-[162px]">
        <Contenitore>
          {/* Come il footer di pingup.ai: colonne a destra con 48 px di margine */}
          <Rivela>
          <Card className="flex flex-col gap-10 p-8 md:flex-row md:justify-between xl:h-[270px] xl:p-0">
            <div className="xl:pl-12 xl:pt-[65px]">
              {/* Al passaggio del mouse il logo si rimpicciolisce con un
                  piccolo rimbalzo (niente animazione con "riduci movimento") */}
              <img
                src="/pingup-logo-horizontal.svg"
                alt="PingUp"
                className="w-[177px] origin-left transition-transform duration-500 hover:scale-[0.88] motion-reduce:transition-none"
                style={{ transitionTimingFunction: "cubic-bezier(0.34, 1.56, 0.64, 1)" }}
              />
              <p className="mt-[9px] text-[16px] leading-6 text-[#7A7A7A]">{t("landing.footer.tagline")}</p>
              <div className="mt-[43px] flex items-center gap-[13px] text-[#121316]" aria-hidden="true">
                <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]">
                  <path fill="currentColor" d="M13.5 24v-9h3l.5-4h-3.5V8.6c0-1 .4-1.6 1.6-1.6H17V3.4c-.5-.1-1.6-.2-2.8-.2-2.9 0-4.7 1.7-4.7 4.9V11H7v4h2.5v9h4Z" />
                </svg>
                <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]">
                  <rect x="2" y="2" width="20" height="20" rx="6" fill="currentColor" />
                  <circle cx="12" cy="12" r="4.2" fill="none" stroke="#F7F7F7" strokeWidth="2" />
                  <circle cx="17.3" cy="6.7" r="1.2" fill="#F7F7F7" />
                </svg>
                <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]">
                  <rect x="3" y="9" width="4" height="12" fill="currentColor" />
                  <circle cx="5" cy="4.8" r="2.3" fill="currentColor" />
                  <path fill="currentColor" d="M10 9h3.8v1.7c.6-1.1 2-2 4-2 3.6 0 4.2 2.4 4.2 5.4V21h-4v-6c0-1.5 0-3.3-2-3.3s-2.3 1.6-2.3 3.2V21H10Z" />
                </svg>
              </div>
            </div>
            <div className="flex gap-10 xl:absolute xl:right-12 xl:top-[65px]">
              <div>
                <p className="text-[17px] font-medium leading-[22px]">Etinet Srl</p>
                <ul className="mt-6 space-y-[14px] text-[16px] leading-[22px] text-[#7A7A7A]">
                  <li><a href="https://pingup.ai/pricing" className="hover:text-[#121316]">{t("landing.footer.pricing")}</a></li>
                  <li><a href="http://etinet.it/" className="hover:text-[#121316]">{t("landing.footer.about")}</a></li>
                  <li><a href="https://pingup.ai/contact-us" className="hover:text-[#121316]">{t("landing.footer.contact")}</a></li>
                </ul>
              </div>
              <div>
                <p className="text-[17px] font-medium leading-[22px]">team.blue</p>
                <ul className="mt-6 space-y-[14px] text-[16px] leading-[22px] text-[#7A7A7A]">
                  <li><a href="https://team.blue" className="hover:text-[#121316]">{t("landing.footer.group")}</a></li>
                  <li>
                    <a href="https://team.blue/careers/" className="inline-flex flex-wrap items-center gap-x-2 gap-y-1 hover:text-[#121316] xl:flex-nowrap xl:whitespace-nowrap">
                      {t("landing.footer.careers")}
                      <span className="whitespace-nowrap rounded-md bg-[#121316] px-1.5 text-[11px] font-semibold leading-5 text-white">
                        {t("landing.footer.hiring")}
                      </span>
                    </a>
                  </li>
                  <li><a href="https://team.blue/our-ecosystem/" className="hover:text-[#121316]">{t("landing.footer.ecosystem")}</a></li>
                </ul>
              </div>
            </div>
          </Card>
          </Rivela>
          <p className="pb-[30px] pt-8 text-center text-[12px] leading-6 text-[#4A4A4A]">
            Copyright © Etinet srl | P.iva 03203840040 |{" "}
            {[
              ["https://pingup.ai/privacy", "Privacy Policy"],
              ["https://www.iubenda.com/privacy-policy/11031416/cookie-policy", "Cookie Policy"],
              ["https://etinet.it/contratto-per-il-trattamento-dati-pingup/", t("landing.footer.dataProcessing")],
              ["https://etinet.it/whistleblowing/", "Whistleblowing"],
            ].map(([href, testo], i) => (
              <React.Fragment key={href}>
                {i > 0 && " | "}
                <a href={href} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-[#121316]">
                  {testo}
                </a>
              </React.Fragment>
            ))}
          </p>
        </Contenitore>
      </footer>
    </div>
  );
};

export default LandingSections;
